import React, { useState, useEffect } from 'react';
import { ArrowLeft, Mail } from 'lucide-react';
import Button from '../../common/Button/Button';
import authService from '../../../api/services/authService';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

const OTPVerification = ({ onBackToLogin, onVerifyOTP, loading: externalLoading = false, userEmail, otpData, userCredentials }) => {
  const [otp, setOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(60); // Start with 60 seconds
  const [error, setError] = useState('');

  // Countdown timer for resend cooldown
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Start timer when component mounts (OTP was already sent)
  useEffect(() => {
    setResendCooldown(60); // Start 60 second timer
  }, []);

  const resendMutation = useMutation({
    mutationFn: async () => {
      if (userCredentials?.inputType === 'phone') {
        let phoneNumber = userCredentials.emailOrPhone;
        if (!phoneNumber.startsWith('+')) phoneNumber = '+91' + phoneNumber;
        return authService.loginMobileOTP(phoneNumber);
      }
      return authService.loginWithEmailOTP(userCredentials.emailOrPhone);
    },
    onSuccess: (result) => {
      if (result.success) {
        toast.success(result.message || 'OTP resent successfully!');
        setOtp(''); // Clear current OTP input
        setResendCooldown(60); // Reset timer to 60 seconds
        setError('');
      } else {
        toast.error(result.error || 'Failed to resend OTP');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to resend OTP. Please try again.');
    }
  });

  const handleResendOTP = () => {
    if (resendCooldown === 0 && !resendMutation.isPending) {
      resendMutation.mutate();
    }
  };

  const handleOTPChange = (index, value) => {
    // Only allow numbers
    const cleanValue = value.replace(/[^0-9]/g, '');
    
    // Handle multi-digit input (like OTP autofill or paste fallback)
    if (cleanValue.length > 1) {
      // If they paste/autofill into a specific box, merge it
      const currentOtpArray = otp.split('');
      let pasteIndex = 0;
      for (let i = index; i < 6; i++) {
        if (pasteIndex < cleanValue.length) {
          currentOtpArray[i] = cleanValue[pasteIndex];
          pasteIndex++;
        }
      }
      const newOtpStr = currentOtpArray.join('').slice(0, 6);
      setOtp(newOtpStr);
      setError('');
      
      const focusIndex = Math.min(index + cleanValue.length, 5);
      const nextInput = document.getElementById(`otp-${focusIndex}`);
      if (nextInput) nextInput.focus();
      return;
    }

    // Handle single character typing
    const newOtpArray = otp.split('');
    // Ensure array is padded with spaces up to the current index if needed
    for (let i = 0; i < index; i++) {
      if (!newOtpArray[i]) newOtpArray[i] = ' ';
    }
    
    newOtpArray[index] = cleanValue;
    // Clean up any trailing spaces
    const combinedOtp = newOtpArray.join('').trimRight();
    setOtp(combinedOtp);
    setError('');

    // Auto-focus next input
    if (cleanValue && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (otp.length === 6) {
        onVerifyOTP(otp);
      } else {
        setError('Please enter a 6-digit code');
      }
      return;
    }

    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }

    // Prevent non-numeric characters
    if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key) && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className="w-full font-sans">
      {/* Logo Header */}
      <div className="flex items-center gap-3.5 mb-8">
        <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
          <img 
            src="/mobiloitte-logo.png" 
            alt="Mobiloitte Logo" 
            className="w-full h-full object-contain"
          />
        </div>
        <div className="flex flex-col text-left">
          <h2 className="text-xl font-bold text-hrms-primary tracking-tight leading-none mb-1">
            Mobiloitte
          </h2>
          <p className="text-[10px] font-bold text-hrms-muted tracking-[0.2em] uppercase leading-none">
            Technologies
          </p>
        </div>
      </div>

      <div className="mb-1">
        <h1 className="text-2xl lg:text-[28px] font-bold text-hrms-text leading-tight tracking-tight text-left">
          Verify Code
        </h1>
      </div>
      <p className="text-hrms-muted text-[13px] leading-relaxed mb-6 max-w-xs text-left">
        Enter the verification code sent to your email to continue.
      </p>

      {/* Email Input Display */}
      <div className="mb-8">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
            <Mail className="h-[15px] w-[15px] text-gray-400" />
          </div>
          <input
            type="email"
            disabled
            value={userEmail || ''}
            className="w-full pl-9 pr-4 py-2.5 bg-[#f5f7fa] border border-gray-100 rounded-xl text-[13px] text-gray-600 font-medium outline-none"
          />
        </div>
      </div>

      <div className="space-y-6">
        <div className="text-center">
          <label className="block text-[14px] font-medium text-hrms-text mb-4">
            Verification Code
          </label>
          <div className="flex justify-center gap-[6px]">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                maxLength={1}
                value={otp[index] || ''}
                onChange={(e) => handleOTPChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedData = e.clipboardData.getData('text');
                  const numbersOnly = pastedData.replace(/[^0-9]/g, '').slice(0, 6);
                  if (!numbersOnly) return;
                  
                  const currentOtpArray = otp.split('');
                  let pasteIndex = 0;
                  
                  for (let i = index; i < 6; i++) {
                    if (pasteIndex < numbersOnly.length) {
                      currentOtpArray[i] = numbersOnly[pasteIndex];
                      pasteIndex++;
                    }
                  }
                  
                  const newOtpStr = currentOtpArray.join('').slice(0, 6);
                  setOtp(newOtpStr);
                  setError('');
                  
                  const focusIndex = Math.min(index + numbersOnly.length, 5);
                  const nextInput = document.getElementById(`otp-${focusIndex}`);
                  if (nextInput) {
                    nextInput.focus();
                  }
                }}
                className={`w-9 h-11 sm:w-[48px] sm:h-[56px] md:w-[52px] md:h-[60px] text-lg sm:text-2xl font-semibold border rounded-xl text-center focus:ring-2 focus:ring-[#0F2942]/50 focus:border-transparent transition-all outline-none ${
                  error ? 'border-red-500 bg-red-50 text-red-600' : 'border-gray-200 bg-white text-gray-800'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="text-center">
          <p className="text-[13px] text-gray-500 mb-2">We've sent a 6-digit code to your email.</p>
          <button
            onClick={handleResendOTP}
            disabled={resendCooldown > 0 || resendMutation.isPending}
            className="text-[14px] text-[#2F54EB] font-medium transition-colors"
          >
            {resendMutation.isPending 
              ? 'Sending...' 
              : resendCooldown > 0 
                ? `Time remaining: ${formatTime(resendCooldown)}` 
                : 'Resend Code'}
          </button>
        </div>

        <button
          type="button"
          className={`w-full py-3 text-white rounded-full font-semibold transition-all duration-300 ${
            externalLoading || (otp.length !== 6 && !error)
              ? 'bg-[#8C9AD5] opacity-70 cursor-not-allowed'
              : 'bg-[#1D39C4] hover:bg-[#10239e] shadow-lg hover:shadow-xl'
          }`}
          onClick={() => {
            if (otp.length === 6) {
              onVerifyOTP(otp);
            } else {
              setError('Please enter a 6-digit code');
            }
          }}
          disabled={externalLoading || (otp.length !== 6 && !error)}
        >
          {externalLoading ? 'Verifying...' : 'Verify & Sign In'}
        </button>

        {error && (
          <div className="bg-red-50 border border-red-100 rounded-lg p-3 animate-shake">
            <p className="text-sm text-red-600 font-medium text-center">{error}</p>
          </div>
        )}

        <div className="relative my-7">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-100"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-3 bg-white text-gray-400 text-xs">or</span>
          </div>
        </div>

        <div className="text-center pb-2">
          <button
            type="button"
            onClick={onBackToLogin}
            className="inline-flex items-center text-[14px] text-[#1D39C4] hover:text-blue-900 font-semibold transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
